
import { Card, Col, Divider, Form, Input, Row, Space, Select } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import ButtonAction from 'components/Common/ButtonAction';
import { ButtonPrimary, ButtonBack, ButtonExcel, ButtonPrint } from 'components/Common/Buttons';
import useTopbarTitleHooks from 'hooks/useTopbarTitleHooks';
import CustomAntdTable from 'components/Common/CustomAntdTable';
import { dismissToast, errorToast, successToast, loadingToastId } from 'utils/Toast';
import Constant from 'utils/Constants';
import { sorterTable } from 'utils/Helper';
import useIsMobileHooks from 'hooks/useIsMobileHooks';
import { PRIVILEGE_ID } from 'utils/AccessPermissionConfig';
import SkeletonPage from 'components/Common/SkeletonPage';
import { ButtonAdvanceFilter } from 'components/Common/ButtonAdvanceFilter';
import Error403 from 'components/Error/Error403';
import BaseAdvanceFilter from 'components/CustomDrawer/BaseAdvanceFilter';
import CellTable from 'components/Common/CellTable';
import { useNavigate, useParams } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import useOrgRepo from "../../../services/repo/useOrgRepo";
import useDivisionRepo from 'services/repo/useDivisionRepo';
import useDepartmentRepo from 'services/repo/useDepartmentRepo';
import useReportRepo from "../../../services/repo/useReportRepo";
import useDownloadReportDepartmentRepo from 'services/repo/useDownloadReportRepo'


export default function DepartmentReport() {
    useTopbarTitleHooks("Report Department");
    const isMobile = useIsMobileHooks()
    const navigate = useNavigate();
    const [form] = Form.useForm()
    const { orgId, divId } = useParams();

    // Repo
    const { isLoading: isLoadingOrgList, getOrgList } = useOrgRepo(false)
    const { isLoading: isLoadingDivList, getDivisionList } = useDivisionRepo(false)
    const { isLoading: isLoadingDeptList, getDepartmentList } = useDepartmentRepo(false)
    const { isLoading: isLoadingReport, getReportDepartmentList } = useReportRepo(false)
    const { isLoading: isLoadingDownloadReportDepartment, getDownloadExcelDepartmentReport } = useDownloadReportDepartmentRepo(false);

    // Privilege
    const isLoadingPrivilege = useRef(true)
    const hasPrivilegePage = useRef(true)
    const [isLoadedPrivilege, setIsLoadedPrivilege] = useState(false)
    const [showButtonAdd, setShowButtonAdd] = useState(false)

    // Table & Filter
    const [data, setData] = useState()
    const [totalData, setTotalData] = useState(0)
    const [isOpenAdvanceFilter, setIsOpenAdvanceFilter] = useState(false)

    const [filters, setFilters] = useState({
        orgId: null,
        divId: null,
        deptId: null
    })

    const [tableParams, setTableParams] = useState({
        pageNumber: 1,
        pageSize: 10,
        filter: { ...filters },
        orderBy: "deptName",
        isAscending: true
    });


    // === Handlers ===
    const onSearch = () => {
        const formValues = form.getFieldsValue();
        const selectedOrg = formValues.orgId;
        const orgId = selectedOrg?.value ?? selectedOrg ?? null;

        setTableParams(prev => ({
            ...prev,
            pageNumber: 1,
            filter: {
                ...filters,
                orgId: orgId,
                divId: divId
            }
        }));
    };

    const onSearchClick = () => {
        onSearch()
        setIsOpenAdvanceFilter(false)
    }

    const handleTableChange = (pagination, filters, sorter) => {
        setTableParams(prevParams => ({
            ...prevParams,
            pageNumber: pagination?.current,
            pageSize: pagination?.pageSize,
            orderBy: sorter?.field || prevParams?.orderBy,
            isAscending: sorterTable(sorter?.order, prevParams?.isAscending)
        }));
        if (pagination.pageSize !== tableParams?.pageSize) {
            setData([]);
        }
    };

    const handleView = async (data) => {
        const key = 'loadingEdit';
        loadingToastId(Constant.message.loading, key);

        const targetPath = APP_ROUTE_PREFIX + "/" + RouteConstant.report.reportPosition(data?.orgId, data?.divId, data?.deptId);
        navigate(targetPath);

        dismissToast(key);
    };


    const handleExport = async () => {
        const key = "loadingExportDepartment";
        loadingToastId("Exporting file...", key);

        try {
            // Ambil dari form
            const formOrg = form.getFieldValue("orgId");   // {label, value} atau number/null (disabled tapi aman)
            const formDiv = form.getFieldValue("divId");   // number/null (disabled tapi aman)
            const formDept = form.getFieldValue("deptId");  // number/null

            // Resolve id (urutan fallback: form -> tableParams.filter -> state filters -> params URL)
            const resolvedOrgId =
                (formOrg?.value ?? formOrg ?? tableParams?.filter?.orgId ?? filters?.orgId ?? orgId) || null;

            const resolvedDivId =
                (formDiv ?? tableParams?.filter?.divId ?? filters?.divId ?? divId) ?? null;

            const resolvedDeptId =
                (formDept ?? tableParams?.filter?.deptId ?? filters?.deptId) ?? null;

            // Validasi minimal
            if (!resolvedOrgId) {
                dismissToast(key);
                errorToast("Please select Organization first.");
                return;
            }
            if (!resolvedDivId) {
                dismissToast(key);
                errorToast("Please select Division first.");
                return;
            }

            // Panggil repo (harus responseType: 'blob')
            const response = await getDownloadExcelDepartmentReport({
                orgId: Number(resolvedOrgId),
                divId: Number(resolvedDivId),
                deptId: resolvedDeptId ? Number(resolvedDeptId) : null, // opsional
            });

            // Jika server balas JSON error sebagai blob
            const contentType =
                response?.headers?.["content-type"] || response?.headers?.get?.("content-type");
            if (contentType && contentType.includes("application/json")) {
                const text = await response.data.text?.();
                try {
                    const json = JSON.parse(text || "{}");
                    throw new Error(json?.message || "Export failed.");
                } catch {
                    throw new Error("Export failed.");
                }
            }

            // Nama file dari Content-Disposition atau fallback
            const dispo =
                response?.headers?.["content-disposition"] ||
                response?.headers?.get?.("content-disposition");
            let fileName = `${getCurrentDateTime()}-Report Department.xlsx`;
            if (dispo) {
                const match = dispo.match(/filename\*=UTF-8''(.+)|filename="?([^"]+)"?/i);
                if (match) fileName = decodeURIComponent(match[1] || match[2]);
            }

            // Buat blob & trigger download
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.setAttribute("download", fileName);
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            dismissToast(key);
            successToast("File successfully downloaded.");
        } catch (err) {
            dismissToast(key);
            errorToast(err?.message || "Export failed.");
        }
    };


    const getCurrentDateTime = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}${month}${day}${hours}${minutes}${seconds}`;
    };


    const fetchData = async () => {

        const payload = {
            ...tableParams,
            filter: {
                ...tableParams.filter,
                orgId: orgId,
                divId: divId // pakai divId dari URL
            }
        }
        const res = await getReportDepartmentList(payload)
        console.log(JSON.stringify(res))
        if (res?.detail == 'OK' && res?.status === 200) {
            setData(res?.data?.content)
            setTotalData(res?.data?.totalElements)
        }
    };


    const fetchCheckPrivilege = async () => {
        setShowButtonAdd(false)
        isLoadingPrivilege.current = false
        hasPrivilegePage.current = true;
        setIsLoadedPrivilege(true)
        // await fetchData();
        dismissToast(PRIVILEGE_ID.AddEditDivision);
    }

    // Org dropdown
    const [orgOptions, setOrgOptions] = useState([])
    const fetchDataOrg = async () => {
        const payload = {
            pageNumber: 1,
            pageSize: 0,
            orderBy: "orgId",
            isAscending: true,
            filter: {
                ...filters
            }
        };
        const res = await getOrgList(payload);

        if (res?.detail === 'OK' && res?.status === 200) {
            const options = res.data.content.map(item => ({
                label: item?.orgName,
                value: item?.orgId,
            }));

            setOrgOptions(options);

            if (options.length === 1) {
                const onlyOption = options[0];

                form.setFieldsValue({ orgId: onlyOption }); // isinya harus { label, value }
                setFilters((prev) => ({ ...prev, orgId: onlyOption.value }));

                setTableParams((prev) => ({
                    ...prev,
                    filter: {
                        ...prev.filter,
                        orgId: onlyOption.value
                    }
                }));

                fetchDataDivision(onlyOption.value);
            }
        }
    };


    const [divOptions, setDivOptions] = useState([])
    const fetchDataDivision = async (orgId) => {
        console.log("🚀 Calling fetchDataDivision with orgId:", orgId); // ⬅ ini wajib muncul

        const payload = {
            pageNumber: 1,
            pageSize: 0,
            filter: {
                divCode: "",
                divName: "",
                orgId: orgId,
                divId: null,
                status: "true",
            },
            orderBy: "divCode",
            isAscending: true
        }
        setDivOptions([])

        const res = await getDivisionList(payload)
        if (res?.detail == 'OK' && res?.status === 200) {
            const options = res?.data?.content.map(item => ({
                value: item?.divId,
                label: item?.divName
            }));

            setDivOptions(options);

            // Auto-select jika hanya ada satu opsi atau ada divId dari URL
            if (options.length === 1) {
                const onlyOption = options[0];
                form.setFieldsValue({ divId: onlyOption.value });
                setFilters((prev) => ({ ...prev, divId: onlyOption.value }));
            } else if (divId) {
                form.setFieldsValue({ divId: divId });
                setFilters((prev) => ({ ...prev, divId: divId }));
            }
        }
    }


    const [deptOptions, setDeptOptions] = useState([]);
    const fetchDataDepartment = async (divId) => {
        const payload = {
            pageNumber: 1,
            pageSize: 0,
            filter: {
                deptCode: "",
                deptName: "",
                divId: divId,
                status: "true"
            },
            orderBy: "deptCode",
            isAscending: true
        };

        const res = await getDepartmentList(payload);

        if (res?.status === 200 && res?.detail === 'OK') {
            const options = res.data.content.map(item => ({
                label: item.deptName,
                value: item.deptId
            }));
            setDeptOptions(options);

            // Auto select jika hanya satu
            if (options.length === 1) {
                const onlyOption = options[0];
                form.setFieldsValue({ deptId: onlyOption.value });
            }
        }
    };



    useEffect(() => {
        fetchCheckPrivilege()
    }, [])


    useEffect(() => {

        if (isLoadedPrivilege) {
            fetchData();
        }

        fetchDataOrg();
        fetchDataDivision();
    }, [JSON.stringify(tableParams)]);

    useEffect(() => {
        const timeOutId = setTimeout(() => {
            if (isLoadedPrivilege && !isMobile) {
                onSearch()
            }
        }, Constant.delayFilter);
        return () => clearTimeout(timeOutId);
    }, [filters]);

    useEffect(() => {
        if (divId) {
            fetchDataDepartment(divId);
        }
    }, [divId]);

    const filterUI = () => {
        return (
            <Form
                layout="horizontal"
                form={form}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={10} md={8}>

                        <Form.Item name='orgId' label="Organization" labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}>
                            <Select
                                style={{ width: '100%' }}
                                allowClear
                                showSearch
                                disabled={true}
                                placeholder='Select Organization'
                                loading={isLoadingOrgList}
                                options={orgOptions}
                                onChange={(value, option) => {
                                    const orgValue = value?.value ?? value ?? null;
                                    console.log("👉 Selected Org Value:", orgValue); // ⬅ Tambahkan ini
                                    console.log("👉 Option Object:", option);         // ⬅ Tambahkan ini
                                    setFilters((prev) => ({
                                        ...prev,
                                        // orgId: value || null, // pastikan null kalau kosong
                                        orgId: orgValue, // pastikan null kalau kosong
                                        divId: null,           // reset divId saat ganti org
                                    }));
                                    form.setFieldsValue({ divFilter: undefined }); // reset divFilter di Form

                                    if (orgValue) {
                                        fetchDataDivision(orgValue);
                                    } else {
                                        setDivOptions([]); // kalau org kosong, kosongkan divisi
                                    }
                                }}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>

                        <Form.Item name="divId" label="Division"
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}>
                            <Select
                                style={{ width: '100%' }}
                                allowClear
                                showSearch
                                placeholder="Select Division"
                                loading={isLoadingDivList}
                                disabled={true}
                                options={divOptions}
                                onChange={(value) => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        divId: value || null,
                                    }));
                                    form.setFieldsValue({ deptId: undefined }); // reset dept
                                    if (value) fetchDataDepartment(value); // Fetch Dept
                                }}

                            />
                        </Form.Item>

                        <Form.Item
                            name="deptId"
                            label="Department"
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                            rules={[
                                {
                                    required: true,
                                    message: "Please select a department",
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder="Select Department"
                                allowClear
                                loading={isLoadingDeptList}
                                style={{ width: '100%' }}
                                options={deptOptions}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>

                    </Col>

                    <Col xs={24} sm={24} md={12}>

                    </Col>

                </Row>
            </Form>
        )
    }

    const columns = [
        {
            title: 'Action',
            key: 'action',
            width: "50px",
            render: (_, record) => {
                const buttons = showButtonAdd ? ['view', 'view'] : ['view']
                return (
                    <ButtonAction
                        buttons={['view']}
                        onView={() => handleView(record)}
                        data={record}
                    />
                )
            }
        },
        {
            title: 'Departement',
            dataIndex: 'deptName',
            sorter: true,
            width: "200px",
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Jumlah Posisi',
            dataIndex: 'posTotal',
            width: "200px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Total FTE',
            dataIndex: 'wbsContentFte',
            width: "200px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Rasio FTE',
            dataIndex: 'wbsRatio',
            width: "200px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Jumlah SDM',
            dataIndex: 'resourceTotal',
            width: "200px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Jumlah SDM Direkomendasikan',
            dataIndex: 'resourceTotalRecommendation',
            width: "200px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Gap',
            dataIndex: 'resourceTotalGap',
            width: "200px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        }
    ];
    return (
        <>
            {hasPrivilegePage.current ?
                <Space direction="vertical" size="large" className='space-vertical-flex'>
                    <Card size="big" bordered={false}>
                        {isLoadingPrivilege.current ? <SkeletonPage /> :
                            <>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={20} xl={18}>
                                        {/* {showButtonAdd && <ButtonAdd onClick={handleAdd} />} */}
                                    </Col>
                                </Row>
                                <Divider dashed className='mb-1 mt-1' />

                                {isMobile ?
                                    <ButtonAdvanceFilter onClick={() => setIsOpenAdvanceFilter(true)} />
                                    :

                                    (
                                        <>
                                            {filterUI()}

                                        </>)
                                }

                                <CustomAntdTable
                                    columns={columns}
                                    dataSource={data}
                                    rowKey={"deptId"}
                                    tableParam={tableParams}
                                    totalData={totalData}
                                    loading={isLoadingReport}
                                    onChange={handleTableChange}
                                // rowClick={handleRowClick}
                                // isOpenDrawer={isOpenDrawer}
                                // idDataHighlight={idHighlightRow}
                                />

                                <Space className="mb-2">
                                    <div>

                                    </div>
                                </Space>

                                <Col span={24} offset={10}>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <ButtonBack onClick={() => navigate(APP_ROUTE_PREFIX + '/' + RouteConstant.report.reportDivision(divId))} />
                                        <ButtonExcel buttonText="Download" onClick={handleExport} />

                                    </div>
                                </Col>
                            </>
                        }
                    </Card>
                </Space> : (<Error403 />)
            }

            {isOpenAdvanceFilter &&
                <BaseAdvanceFilter
                    onClose={() => setIsOpenAdvanceFilter(false)}
                    onSearchClick={onSearchClick}>
                    {filterUI()}
                </BaseAdvanceFilter>}
        </>
    )
}
